import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Box, Paper, Typography, Grid, Card, CardActionArea, CardContent, TextField, IconButton, Stack, Button, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Alert, Avatar, Checkbox, Chip, Toolbar, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Upload as UploadIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import * as moodboardService from '../../services/moodboardService';
import { getMoodboardMetadata, saveMoodboardMetadata } from '../../services/storyToolsService';

const DropZone = styled(Paper)(({ theme, isdragging }) => ({
  padding: theme.spacing(2),
  border: `2px dashed ${theme.palette.divider}`,
  background: theme.palette.mode === 'dark' ? '#1e2124' : '#fafafa',
  textAlign: 'center',
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  outline: 'none',
  ...(isdragging === 'true' && {
    borderColor: theme.palette.primary.main,
    background: theme.palette.mode === 'dark' ? '#19202a' : '#f0f7ff'
  })
}));

const isImageName = (name = '') => /\.(png|jpe?g|gif|webp|tif?f)$/i.test(name);

export default function ProjectMoodboardTab({ projectId }) {
  const [allFiles, setAllFiles] = useState([]);
  const [images, setImages] = useState([]);
  const [order, setOrder] = useState([]);
  const [captions, setCaptions] = useState({});
  const [tags, setTags] = useState({}); // id -> [tag]
  const [tagInputs, setTagInputs] = useState({}); // id -> input text
  const [selected, setSelected] = useState(new Set());
  const [savingCaption, setSavingCaption] = useState({}); // id -> boolean (for saved indicator)
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState({ open: false, index: -1, url: '', title: '' });
  const [uploadProgress, setUploadProgress] = useState({}); // tempId -> percent
  const gridKeyHandlerRef = useRef(null);
  const inputRef = useRef(null);

  const storageKey = useMemo(() => `moodboard:${projectId}`, [projectId]);
  const captionsKey = useMemo(() => `moodboard:${projectId}:captions`, [projectId]);
  const tagsKey = useMemo(() => `moodboard:${projectId}:tags`, [projectId]);
  const saveTimerRef = useRef(null);
  const isLoadedRef = useRef(false);

  // Save metadata to API with debouncing and localStorage backup
  const saveMetadataToAPI = useCallback(async (orderData, captionsData, tagsData) => {
    // Always save to localStorage immediately for offline access
    try {
      localStorage.setItem(storageKey, JSON.stringify(orderData));
      localStorage.setItem(captionsKey, JSON.stringify(captionsData));
      localStorage.setItem(tagsKey, JSON.stringify(tagsData));
    } catch (err) {
      console.error('Error saving to localStorage:', err);
    }

    // Debounce API save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(async () => {
      try {
        await saveMoodboardMetadata(projectId, {
          order: orderData,
          captions: captionsData,
          tags: tagsData
        });
        console.log('✅ Moodboard metadata saved to API');
      } catch (err) {
        console.error('Error saving moodboard metadata to API (localStorage backup active):', err);
      }
    }, 1500);
  }, [projectId, storageKey, captionsKey, tagsKey]);

  const load = async () => {
    try {
      const list = await moodboardService.list(projectId);
      setAllFiles(list || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load files');
    }
  };

  useEffect(() => {
    if (!projectId) return;
    
    const loadMetadata = async () => {
      try {
        // Try to load from API first
        const response = await getMoodboardMetadata(projectId);
        if (response?.data?.metadata) {
          const { order: savedOrder, images } = response.data.metadata;
          setOrder(Array.isArray(savedOrder) ? savedOrder : []);
          
          // Convert images array to captions/tags objects
          const captionsObj = {};
          const tagsObj = {};
          (images || []).forEach(img => {
            if (img.fileId) {
              captionsObj[img.fileId] = img.caption || '';
              tagsObj[img.fileId] = img.tags || [];
            }
          });
          setCaptions(captionsObj);
          setTags(tagsObj);
          console.log('✅ Loaded moodboard metadata from API');
          
          // Update localStorage with API data
          localStorage.setItem(storageKey, JSON.stringify(savedOrder || []));
          localStorage.setItem(captionsKey, JSON.stringify(captionsObj));
          localStorage.setItem(tagsKey, JSON.stringify(tagsObj));
        } else {
          throw new Error('No data from API');
        }
      } catch (err) {
        console.log('🔍 No API metadata, checking localStorage...');
        // Fallback to localStorage
        try {
          const savedOrder = JSON.parse(localStorage.getItem(storageKey) || '[]');
          const savedCaptions = JSON.parse(localStorage.getItem(captionsKey) || '{}');
          const savedTags = JSON.parse(localStorage.getItem(tagsKey) || '{}');
          setOrder(Array.isArray(savedOrder) ? savedOrder : []);
          setCaptions(savedCaptions || {});
          setTags(savedTags || {});
          
          // Migrate to API if we have data
          if (savedOrder.length > 0 || Object.keys(savedCaptions).length > 0 || Object.keys(savedTags).length > 0) {
            setTimeout(() => {
              saveMoodboardMetadata(projectId, {
                order: savedOrder,
                captions: savedCaptions,
                tags: savedTags
              })
                .then(() => console.log('✅ Migrated localStorage moodboard metadata to API'))
                .catch(e => console.log('Migration to API pending:', e.message));
            }, 2000);
          }
        } catch (localErr) {
          console.error('Error loading from localStorage:', localErr);
        }
      }
      isLoadedRef.current = true;
    };
    
    loadMetadata();
    load();
    
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    // filter images from files, keep current order where possible
    const imgs = (allFiles || []).filter(f => isImageName(f.name || f.filename));
    const map = new Map(imgs.map(f => [f.id || f._id || f.name || f.filename, f]));
    const ordered = [];
    for (const id of order) {
      const f = map.get(id);
      if (f) {
        ordered.push(f);
        map.delete(id);
      }
    }
    for (const [, f] of map) ordered.push(f);
    setImages(ordered);
  }, [allFiles, order]);

  // persist order/captions/tags to API with localStorage backup
  useEffect(() => {
    if (isLoadedRef.current) {
      saveMetadataToAPI(order, captions, tags);
    }
  }, [order, captions, tags, saveMetadataToAPI]);

  // Drag-and-drop removed for Pinterest-style layout. Order remains from upload/initial state.

  const onPick = () => inputRef.current?.click();
  const onChangeInput = async (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;
    try {
      // Optimistic insert: show local object URLs immediately
      const temps = files.map((f) => {
        const url = URL.createObjectURL(f);
        const id = `temp-${Math.random().toString(36).slice(2)}`;
        return { id, name: f.name, size: f.size, createdAt: Date.now(), url, __temp: true };
      });
      setAllFiles(prev => [...(prev || []), ...temps]);
      setOrder(prev => [...prev, ...temps.map(t => t.id)]);
      // Upload sequentially per file to show per-file progress
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const temp = temps[i];
        const tempId = temp.id;
        setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));
        const uploadedArr = await moodboardService.upload(projectId, [file], (p) => {
          setUploadProgress(prev => ({ ...prev, [tempId]: p }));
        });
        const uploadedFile = (uploadedArr || [])[0];
        // Replace the specific temp with the real uploaded file
        setAllFiles(prev => {
          const next = (prev || []).filter(p => (p.id || p._id || p.name || p.filename) !== tempId);
          try { URL.revokeObjectURL(temp.url); } catch {}
          return [...next, uploadedFile];
        });
        setOrder(prev => prev.map(id => id === tempId ? (uploadedFile.id || uploadedFile._id || uploadedFile.name || uploadedFile.filename) : id));
        setUploadProgress(prev => { const { [tempId]: _, ...rest } = prev; return rest; });
      }
    } catch (e1) {
      setError(e1?.response?.data?.message || e1?.message || 'Upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const openPreview = (f) => {
    const id = f.id || f._id || f.name || f.filename;
    const idx = images.findIndex(img => (img.id || img._id || img.name || img.filename) === id);
    const url = getFileUrl(f);
    setPreview({ open: true, index: idx, url, title: f.name || f.filename });
  };

  const updatePreviewByIndex = (newIndex) => {
    if (!images || images.length === 0) return;
    const bounded = (newIndex + images.length) % images.length;
    const f = images[bounded];
    const url = getFileUrl(f);
    setPreview({ open: true, index: bounded, url, title: f.name || f.filename });
  };

  const updateCaption = (id, value) => {
    setCaptions(prev => ({ ...prev, [id]: value }));
    // show saved indicator briefly (localStorage persistence happens via effect)
    setSavingCaption(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setSavingCaption(prev => ({ ...prev, [id]: false }));
    }, 800);
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(images.map(f => f.id || f._id || f.name || f.filename)));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    for (const id of ids) {
      try {
        await moodboardService.remove(projectId, id);
      } catch {}
    }
    setAllFiles(prev => (prev || []).filter(f => !selected.has(f.id || f._id || f.name || f.filename)));
    setOrder(prev => prev.filter(id => !selected.has(id)));
    clearSelection();
  };

  const addTag = (id) => {
    const val = (tagInputs[id] || '').trim();
    if (!val) return;
    setTags(prev => ({ ...prev, [id]: Array.from(new Set([...(prev[id] || []), val])) }));
    setTagInputs(prev => ({ ...prev, [id]: '' }));
  };
  const removeTag = (id, tag) => {
    setTags(prev => ({ ...prev, [id]: (prev[id] || []).filter(t => t !== tag) }));
  };

  const getFileUrl = (f) => {
    const base = f.url || `/uploads/moodboard/${projectId}/${encodeURIComponent(f.name || f.filename || f.id)}`;
    if (typeof base !== 'string') return '';

    if (base.startsWith('blob:')) return base;

    const apiBase = process.env.REACT_APP_API_BASE_URL;
    const resolvedBase = (apiBase && base.startsWith('/'))
      ? `${apiBase.replace(/\/$/, '')}${base}`
      : base;

    return `${resolvedBase}?v=${encodeURIComponent(f.createdAt || Date.now())}`;
  };

  return (
    <Box sx={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', pr: 0.5 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Moodboard</Typography>
            <Chip size="small" label={`${images.length} ${images.length === 1 ? 'image' : 'images'}`} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" startIcon={<UploadIcon />} onClick={onPick} aria-label="add images to moodboard">
              Add Images
            </Button>
            <input ref={inputRef} type="file" multiple accept="image/*" onChange={onChangeInput} style={{ display: 'none' }} />
          </Stack>
        </Stack>
        <Toolbar variant="dense" disableGutters sx={{ mt: 1, gap: 1 }}>
          <Button size="small" onClick={selectAll} aria-label="select all images">Select All</Button>
          <Button size="small" onClick={clearSelection} aria-label="clear selection">Clear</Button>
          <Button size="small" color="error" variant="outlined" onClick={bulkDelete} disabled={selected.size === 0} startIcon={<DeleteIcon />} aria-label="delete selected images">Delete Selected</Button>
          <Typography variant="caption" sx={{ ml: 'auto' }} color="text.secondary">{selected.size} selected</Typography>
        </Toolbar>
      </Paper>

      {/* Keyboard handler wrapper for grid */}
      <Box
        tabIndex={0}
        aria-label="moodboard grid"
        onKeyDown={(e) => {
          if (e.ctrlKey && (e.key === 'a' || e.key === 'A')) {
            e.preventDefault();
            selectAll();
          }
          if (e.key === 'Delete') {
            e.preventDefault();
            if (selected.size > 0) bulkDelete();
          }
        }}
        sx={{ outline: 'none' }}
      >

      {/* Empty State */}
      {images.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', borderWidth: 1 }}>
          <UploadIcon sx={{ fontSize: 40, mb: 1, opacity: 0.6 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>No images yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Get started by uploading reference images for your project.</Typography>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={onPick}>Upload Images</Button>
        </Paper>
      ) : (
      /* Masonry (Pinterest-style) layout */
      <Box sx={{ columnCount: { xs: 1, sm: 6, md: 8, lg: 10, xl: 12 }, columnGap: 0.3, px: 0.1, pt: 0.1, pb: 0.1 }}>
        {images.map((f) => {
          const id = f.id || f._id || f.name || f.filename;
          return (
            <Box 
              key={String(id)} 
              sx={{ breakInside: 'avoid', display: 'inline-block', width: '100%', pb: 0.125 }}
            >
              <Card sx={{ 
                borderRadius: 1, 
                overflow: 'hidden', 
                border: (theme) => `1px solid ${theme.palette.divider}`,
                outline: (theme) => selected.has(id) ? `2px solid ${theme.palette.primary.main}` : 'none',
                outlineOffset: selected.has(id) ? 0 : 0,
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}>
                <Tooltip title={(f.name || f.filename) || ''}>
                  <CardActionArea onClick={() => openPreview(f)}>
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2, bgcolor: 'background.paper', borderRadius: 1 }} onClick={(e)=>e.stopPropagation()}>
                      <Checkbox size="small" checked={selected.has(id)} onChange={() => toggleSelect(id)} />
                    </Box>
                    <img
                      src={getFileUrl(f)}
                      alt={f.name || f.filename}
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    {f.__temp && (
                      <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: 0.5, bgcolor: 'rgba(0,0,0,0.25)' }}>
                        <LinearProgress variant="determinate" value={uploadProgress[id] || 0} sx={{ height: 4, borderRadius: 1 }} />
                      </Box>
                    )}
                  </Box>
                  </CardActionArea>
                </Tooltip>
                <CardContent sx={{ p: 0.08 }}>
                  {/* Tags */}
                  <Stack direction="row" spacing={0.25} flexWrap="wrap" useFlexGap sx={{ mb: 0.25 }}>
                    {(tags[id] || []).map(tag => (
                      <Chip key={tag} size="small" label={tag} onDelete={() => removeTag(id, tag)} sx={{ borderRadius: 1 }} />
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={0.25} sx={{ mb: 0.25 }}>
                    <TextField size="small" placeholder="Add tag" value={tagInputs[id] || ''} onChange={(e)=>setTagInputs(prev=>({ ...prev, [id]: e.target.value }))} onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); addTag(id); } }} sx={{ flex: 1 }} />
                    <Button size="small" variant="outlined" onClick={()=>addTag(id)}>Add</Button>
                  </Stack>
                  {/* Caption with counter and saved indicator */}
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Add caption"
                    value={captions[id] || ''}
                    onChange={(e) => updateCaption(id, e.target.value)}
                    inputProps={{ maxLength: 200 }}
                    InputProps={{ sx: { borderRadius: 1 } }}
                  />
                  <Stack direction="row" spacing={0.25} justifyContent="space-between" sx={{ mt: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">{(captions[id] || '').length}/200</Typography>
                    {savingCaption[id] ? (
                      <Typography variant="caption" color="success.main">Saved</Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled"> </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          );
        })}
        </Box>
        )}

      <Dialog 
        open={preview.open} 
        onClose={() => setPreview({ open: false, index: -1, url: '', title: '' })} 
        maxWidth="lg" 
        fullWidth
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); updatePreviewByIndex((preview.index || 0) - 1); }
          if (e.key === 'ArrowRight') { e.preventDefault(); updatePreviewByIndex((preview.index || 0) + 1); }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{preview.title || 'Preview'}</Typography>
          <Box>
            <IconButton onClick={() => updatePreviewByIndex((preview.index || 0) - 1)} aria-label="previous image">
              <ChevronLeftIcon />
            </IconButton>
            <IconButton onClick={() => updatePreviewByIndex((preview.index || 0) + 1)} aria-label="next image">
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '80vh', overflow: 'auto' }}>
          <Box sx={{ textAlign: 'center' }}>
            {preview.url && (
              <img src={preview.url} alt={preview.title} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview({ open: false, index: -1, url: '', title: '' })}>Close</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
}
